/**
 * AI Summary HTML normalization.
 *
 * The dashboard AI Summary is model-generated HTML injected via
 * dangerouslySetInnerHTML. The prompt only asks the model to color the
 * positive/negative *delta* spans (green #00c853 / red #ff5252); everything
 * else should inherit the app theme. In practice the model (Opus 5) also bakes
 * a hardcoded dark text color into the prose, which is readable on light mode's
 * white background but invisible on the dark-mode background.
 *
 * `sanitizeAiSummaryHtml` strips every inline `color:` declaration EXCEPT the
 * two delta colors, so the prose falls back to the theme's text color (set on
 * the container) while the green/red deltas — which read fine in both themes —
 * are preserved. Non-color style declarations (margin, font-weight, …) are
 * left untouched. Pure and framework-agnostic so it stays unit-testable.
 */

/** The two delta colors the prompt instructs the model to emit. */
const DELTA_COLORS = new Set(["#00c853", "#ff5252"]);

/** Normalize an inline `color` value for comparison (lowercase, no spaces). */
function normColor(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, "");
}

/**
 * Remove hardcoded text colors from AI-summary HTML, keeping only the
 * green/red delta colors. Returns the input unchanged when it is empty.
 */
export function sanitizeAiSummaryHtml(html: string | null): string | null {
  if (!html) return html;
  // Rewrite each style="…" / style='…' attribute, dropping non-delta `color`.
  // The optional leading whitespace is captured so a fully-removed attribute
  // leaves no dangling space (e.g. `<p style="color:#111">` -> `<p>`).
  return html.replace(
    /(\s*)style\s*=\s*(["'])([\s\S]*?)\2/gi,
    (_match, lead: string, quote: string, decls: string) => {
      const kept = decls
        .split(";")
        .map((d) => d.trim())
        .filter(Boolean)
        .filter((decl) => {
          const idx = decl.indexOf(":");
          if (idx === -1) return true;
          const prop = decl.slice(0, idx).trim().toLowerCase();
          if (prop !== "color") return true; // keep non-color declarations
          const val = normColor(decl.slice(idx + 1));
          return DELTA_COLORS.has(val); // keep delta colors, drop the rest
        });
      return kept.length
        ? `${lead}style=${quote}${kept.join("; ")}${quote}`
        : "";
    }
  );
}
